import { TradingDeliveryType } from '@ebalance/proto/bfftypes/v1/trading_delivery_type_pb'
import i18next from 'i18next'

/**
 * 受渡区分 (TradingDeliveryType enum) → 日本語ラベル (canonical)。
 *
 * ラベル文言は `common:deliveryType.*` に集約する。
 * 値セットは spec (`docs/specs/supply/bilateral-trade-list.md:127,159,452` #4800
 * 2026-08-06、`docs/specs/admin/data/uploads/bilateral-trade.md:127`) の 6 値:
 * BG / JEPX / EEX / TOCOM / ICE / スワップ。旧 eClear は撤去し、
 * 相対 JEPX 渡し契約 (JEPX) を BG 経由グループに追加する
 * (backend `physicalDeliveryTypes = {BG, Eclear, JEPX}` / `trading_delivery_type.go:67`)。
 * 「常時バックアップ」 は受渡区分ではなく約定経路 (TradingExecutionRoute の常時BU)
 * として管理する (issue #1586)。
 *
 * ECLEAR case を残しているのは backend domain enum (`TradingDeliveryTypeEclear = 2`)
 * が legacy レコード表示 / plan submission 経路のために残っているため
 * (= 万一 ECLEAR 値が UI に流れても badge が空にならない防御)。新規選択・フィルタは
 * `DELIVERY_TYPE_GROUPS` / `DELIVERY_TYPE_OPTIONS` (6 値・ECLEAR 除外) を SoT とする。
 */
export function convertDeliveryTypeLabel(v: TradingDeliveryType): string {
  const t = i18next.t.bind(i18next)
  switch (v) {
    case TradingDeliveryType.BG:
      return t('common:deliveryType.bg')
    case TradingDeliveryType.JEPX:
      return t('common:deliveryType.jepx')
    case TradingDeliveryType.ECLEAR:
      return t('common:deliveryType.eclear')
    case TradingDeliveryType.SWAP:
      return t('common:deliveryType.swap')
    case TradingDeliveryType.TOCOM:
      return t('common:deliveryType.tocom')
    case TradingDeliveryType.EEX:
      return t('common:deliveryType.eex')
    case TradingDeliveryType.ICE:
      return t('common:deliveryType.ice')
    default:
      return ''
  }
}

/**
 * 受渡区分の 2 グループ構造 (spec `bilateral-trade-list.md:127` の値セット 6 値に準拠)。
 * select はフラット表示 (= グループ見出しを出さない) のため、 本構造は
 * option 並び順 (BG経由 → 物理受渡なし) と `BG_ROUTE_SET` (= 派生属性導出) の
 * 正本として使う。 グループ見出し i18n は select フラット化に伴い廃止した。
 */
export type DeliveryTypeGroup = {
  /** このグループに属する TradingDeliveryType 値 */
  options: TradingDeliveryType[]
}

export const DELIVERY_TYPE_GROUPS: DeliveryTypeGroup[] = [
  {
    // BG 経由 (物理受渡): BG + JEPX (相対 JEPX 渡し契約)。
    // 旧 eClear は spec `bilateral-trade-list.md:127` (#4800 2026-08-06) で撤去。
    options: [TradingDeliveryType.BG, TradingDeliveryType.JEPX],
  },
  {
    options: [
      TradingDeliveryType.EEX,
      TradingDeliveryType.TOCOM,
      TradingDeliveryType.ICE,
      TradingDeliveryType.SWAP,
    ],
  },
]

/**
 * 受渡区分の選択肢をフラット (= グループ見出しなし) に並べた一覧。
 * filter / form の select で `<option>` を直接描画する用 (= optgroup 見出しは出さない)。
 * 並び順は DELIVERY_TYPE_GROUPS の定義順 (BG経由 → 物理受渡なし) を踏襲する。
 */
export const DELIVERY_TYPE_OPTIONS: TradingDeliveryType[] =
  DELIVERY_TYPE_GROUPS.flatMap((group) => group.options)

// BG 経由グループ (physical=true かつ requires_settlement_price_area=false):
// BG (=1) + JEPX (=8)。backend `physicalDeliveryTypes` (= {BG, Eclear, JEPX}) と
// 合わせ、legacy ECLEAR (=2) も明示的に物理側 (BG_ROUTE) に含める。
const BG_ROUTE_SET = new Set<TradingDeliveryType>([
  ...DELIVERY_TYPE_GROUPS[0].options,
  TradingDeliveryType.ECLEAR,
])

/**
 * 物理受渡かどうかを enum から導出する。
 * is_physical = true: BG経由 (1〜2)。 物理受渡なし (差金決済、 4〜7) は false。
 */
export function isPhysical(v: TradingDeliveryType): boolean {
  return BG_ROUTE_SET.has(v)
}

/**
 * 決済エリアプライス参照が必要かどうかを enum から導出する。
 * requires_settlement_price_area = true: 物理受渡なし (差金決済、 4〜7)。
 */
export function requiresSettlementPriceArea(v: TradingDeliveryType): boolean {
  return !BG_ROUTE_SET.has(v) && v !== TradingDeliveryType.UNSPECIFIED
}

/**
 * 受渡区分の 2 種別を解決する。
 *   - BG経由 (物理受渡) → 'bgRoute'
 *   - 物理受渡なし (差金決済) → 'financialSettlement'
 */
export type DeliveryTypeKind = 'bgRoute' | 'financialSettlement'

export function resolveDeliveryTypeKind(
  v: TradingDeliveryType,
): DeliveryTypeKind {
  return isPhysical(v) ? 'bgRoute' : 'financialSettlement'
}
